/**
 * Eigenbau: Metarrs Rückfrage mit Antwortmöglichkeiten zum Anklicken.
 *
 * Vorher gab es nur ein leeres Freitextfeld — und das auch nur im
 * Assistenten-Abschnitt der Aufgabenseite; die Metarr-Seite zeigte, dass eine
 * Rückfrage wartet, ohne einen Weg, sie zu beantworten.
 *
 * Ein leeres Feld verlangt vom Menschen, die Sprache des Modells zu erraten.
 * Wer die Frage stellt, weiß am besten, welche Antworten seinen nächsten
 * Schritt bestimmen, und liefert sie deshalb mit (`rueckfrageOptionen`). Der
 * Freitext bleibt daneben: Keine Auswahl trifft jeden Fall.
 *
 * `mehrfach` entscheidet, ob sich Optionen kombinieren lassen. Bei Maßnahmen
 * ist das der Normalfall („Status setzen **und** zuweisen"), bei einem
 * Entweder-oder wäre es Unsinn — deshalb sagt es das Modell, nicht wir.
 *
 * Ältere Läufe haben keine Optionen (`null`). Dann bleibt genau die Maske von
 * vorher: ein Feld, ein Knopf.
 */

import { useState } from "react";
import { Button } from "@/components/Button";
import { Label } from "@/components/Label";
import { Textarea } from "@/components/Textarea";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { cx, focusRing } from "@/lib/utils";

export function Rueckfragekarte({
  frage,
  entscheidung,
  optionen,
  mehrfach,
  laeuft,
  onAntworten,
  onAbbrechen,
  abbruchLaeuft = false,
}: {
  frage: string;
  entscheidung: string | null;
  optionen: string[] | null;
  mehrfach: boolean | null;
  laeuft: boolean;
  onAntworten: (antwort: string) => void;
  /**
   * Fehlt der Handgriff, bleibt die Karte wie zuvor: antworten oder liegen
   * lassen. Nicht jede Einbaustelle kann einen Lauf beenden.
   */
  onAbbrechen?: (() => void) | undefined;
  abbruchLaeuft?: boolean | undefined;
}) {
  const [gewaehlt, setzeGewaehlt] = useState<string[]>([]);
  const [freitext, setzeFreitext] = useState("");

  const mehrfachauswahl = mehrfach === true;
  const hatOptionen = (optionen?.length ?? 0) > 0;

  /*
   * Die Antwort ist, was gewählt wurde, und was dazugeschrieben steht. Beides
   * geht als ein Text an den Lauf — das Modell bekommt seine eigenen Worte
   * zurück und muss nichts zuordnen.
   */
  const antwort = [...gewaehlt, freitext.trim()]
    .filter((teil) => teil.length > 0)
    .join("\n");

  const umschalten = (option: string) => {
    setzeGewaehlt((vorher) => {
      if (!mehrfachauswahl) return vorher[0] === option ? [] : [option];
      return vorher.includes(option)
        ? vorher.filter((eintrag) => eintrag !== option)
        : [...vorher, option];
    });
  };

  return (
    <div className="bg-wisch p-5">
      <p className="text-[14.5px] font-light leading-[1.55] text-tinte">
        {frage}
      </p>
      {entscheidung ? (
        <p className="mt-2.5 max-w-[62ch] text-[12.5px] leading-[1.6] text-tinte3">
          <span className="font-medium text-tinte2">Davon hängt ab:</span>{" "}
          {entscheidung}
        </p>
      ) : null}

      {hatOptionen ? (
        <div className="mt-5">
          <Mikrolabel grad={11}>
            {mehrfachauswahl
              ? "Mehreres wählbar"
              : "Eine Antwort wählen"}
          </Mikrolabel>
          {/*
            `aria-pressed` statt Kästchen: Die Fläche **ist** die Auswahl, und
            ein Schalter mit gedrücktem Zustand sagt das Vorleseprogrammen
            richtig — auch dann, wenn mehrere gleichzeitig gedrückt sind.
          */}
          <div className="mt-2.5 flex flex-col gap-1.5">
            {optionen!.map((option) => {
              const aktiv = gewaehlt.includes(option);
              return (
                <button
                  key={option}
                  type="button"
                  aria-pressed={aktiv}
                  onClick={() => umschalten(option)}
                  className={cx(
                    "flex items-start gap-3 border px-3.5 py-2.5 text-left text-[13.5px] leading-[1.5] transition-colors duration-150 ease-aus",
                    aktiv
                      ? "border-akzent bg-papier text-tinte"
                      : "border-linie2 text-tinte2 hover:border-linie hover:text-tinte",
                    focusRing,
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cx(
                      "mt-[3px] size-2.5 flex-none border",
                      mehrfachauswahl ? "" : "rounded-full",
                      aktiv
                        ? "border-akzent bg-akzent"
                        : "border-linie bg-transparent",
                    )}
                  />
                  {option}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}

      <div className="mt-5 space-y-1.5">
        <Label htmlFor="metarr-rueckfrage-freitext">
          {hatOptionen ? "Ergänzen oder eigene Antwort" : "Antwort"}
        </Label>
        <Textarea
          id="metarr-rueckfrage-freitext"
          rows={hatOptionen ? 2 : 3}
          value={freitext}
          onChange={(ereignis) => setzeFreitext(ereignis.target.value)}
        />
      </div>

      {/*
        Zwei Ausgänge, nicht einer. Eine Rückfrage kannte vorher nur „antworten"
        — wer die Frage nicht beantworten wollte, ließ sie stehen, und sie
        zählte für immer im Zähler mit. Verworfen wird als Nebenhandlung
        gesetzt: gleiche Zeile, aber leiser, damit sie nicht versehentlich
        die Antwort ersetzt.
      */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={() => onAntworten(antwort)}
          disabled={laeuft || abbruchLaeuft || antwort.length === 0}
          isLoading={laeuft}
          loadingText="Wird gesendet …"
        >
          Antwort senden
        </Button>
        {onAbbrechen ? (
          <Button
            type="button"
            variant="secondary"
            onClick={onAbbrechen}
            disabled={laeuft || abbruchLaeuft}
            isLoading={abbruchLaeuft}
            loadingText="Wird verworfen …"
          >
            Rückfrage verwerfen
          </Button>
        ) : null}
      </div>
      {onAbbrechen ? (
        <p className="mt-2.5 max-w-[62ch] text-[12px] leading-[1.6] text-tinte3">
          Verworfen beendet diesen Lauf. Die Frage und ihr Verlauf bleiben
          erhalten — gelöscht wird nichts.
        </p>
      ) : null}
    </div>
  );
}
